/**
 * @fileoverview The org dashboard's Today card: what is on today, read from
 * `GET /org/calendar` for one day (the Calendar page's own read, so the two
 * never disagree about a meeting).
 *
 * Mirrors `mobile/src/lib/orgToday.js`; keep the two in step.
 *
 * **The filters are program types**, the kinds of teaching an organization
 * runs: Classes, Camps, Private lessons. Only the types the organization has
 * are offered, and none at all when it has one, since "All" and the only
 * other choice would always agree.
 *
 * **Private lessons are named, never listed.** Their schedule is bookings in
 * Kalver Scheduler, which Hans cannot enumerate (the calendar says the same),
 * so the card says where they are rather than reading as a quiet day.
 */

/** Program types in the order the filters offer them, with their plural labels. */
export const TODAY_TYPES = [
  { id: "class", label: "Classes" },
  { id: "camp", label: "Camps" },
  { id: "private_lessons", label: "Private lessons" },
];

export const ALL_TYPES = "all";

/** "HH:MM" as minutes after midnight, or null. */
function minutesOf(hhmm) {
  const [h, m] = String(hhmm || "").split(":").map(Number);
  return Number.isFinite(h) && String(hhmm || "").includes(":") ? h * 60 + (m || 0) : null;
}

/** The program type of each program the calendar answered with, by id. */
function typesById(data) {
  const types = new Map();
  for (const p of data?.programs || []) types.set(p.program_id, p.program_type || "class");
  for (const p of data?.unshown_programs || []) types.set(p.program_id, p.program_type || "private_lessons");
  return types;
}

/** A meeting's program type: a class-based one, since only those have meetings. */
function typeOf(m, types) {
  return types.get(m.program_id) || "class";
}

/**
 * Today's meetings of one type (or every type), in the order they start.
 *
 * @param {Object|null} data - The `/org/calendar` answer for today.
 * @param {string} type - A `TODAY_TYPES` id or `ALL_TYPES`.
 * @returns {Object[]}
 */
export function todayMeetings(data, type = ALL_TYPES) {
  const types = typesById(data);
  return (data?.meetings || [])
    .filter((m) => type === ALL_TYPES || typeOf(m, types) === type)
    .slice()
    .sort((a, b) => (minutesOf(a.time_local) ?? 0) - (minutesOf(b.time_local) ?? 0));
}

/**
 * The filters to offer: All, then each type the organization runs, with how
 * many meetings it has today (none for private lessons, which Hans cannot
 * count). Empty when there is one type or none.
 *
 * @param {Object|null} data
 * @returns {Array<{id: string, label: string, count: number|null}>}
 */
export function todayFilters(data) {
  const types = typesById(data);
  const present = new Set(types.values());
  const offered = TODAY_TYPES.filter((t) => present.has(t.id));
  if (offered.length < 2) return [];
  const meetings = data?.meetings || [];
  return [
    { id: ALL_TYPES, label: "All", count: meetings.length },
    ...offered.map((t) => ({
      id: t.id,
      label: t.label,
      count: t.id === "private_lessons" ? null : meetings.filter((m) => typeOf(m, types) === t.id).length,
    })),
  ];
}

/**
 * Where today's private lessons are, when the organization runs any: named,
 * so a list without them is not read as all there is.
 *
 * @param {Object|null} data
 * @returns {string|null}
 */
export function privateLessonsNote(data) {
  const titles = (data?.unshown_programs || []).map((p) => p.title).filter(Boolean);
  if (!titles.length) return null;
  return `Private lessons in ${titles.join(", ")} are booked in Kalver Scheduler, so they aren't listed here.`;
}

/**
 * The date and time of day it is now in `zone` (the organization's), so a
 * meeting's local time is compared with the clock of the place it happens.
 *
 * @param {string|null} zone - An IANA zone; the device's own when absent.
 * @param {Date} [at]
 * @returns {{date: string, minutes: number}}
 */
export function nowIn(zone, at = new Date()) {
  let parts;
  try {
    parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone || undefined,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(at);
  } catch {
    parts = new Intl.DateTimeFormat("en-US", {
      year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
    }).formatToParts(at);
  }
  const part = (type) => parts.find((p) => p.type === type)?.value || "00";
  return { date: `${part("year")}-${part("month")}-${part("day")}`, minutes: Number(part("hour")) * 60 + Number(part("minute")) };
}

/**
 * Where a meeting stands against now: "cancelled", "done" once it has ended,
 * "now" while it runs, else "next". A meeting with no length is done once it
 * has started.
 *
 * @param {Object} m - A `/org/calendar` meeting.
 * @param {{date: string, minutes: number}} now - From `nowIn`.
 * @returns {"cancelled"|"done"|"now"|"next"}
 */
export function meetingPhase(m, now) {
  if (m.status === "cancelled") return "cancelled";
  if (m.date !== now.date) return m.date < now.date ? "done" : "next";
  const start = minutesOf(m.time_local);
  if (start === null || now.minutes < start) return "next";
  return now.minutes < start + (m.duration_minutes || 0) ? "now" : "done";
}

/** Who a meeting's row names: its substitute, else everyone teaching it. */
export function todayTeacher(m) {
  if (m.substitute_name) return m.substitute_name;
  const names = (m.instructor_names?.length ? m.instructor_names : [m.instructor_name]).filter(Boolean);
  return names.join(" and ") || null;
}

/**
 * A meeting nobody is down to teach: no instructor on its class and no
 * substitute named, and not cancelled. Its row says "No instructor", so the
 * gap reads as one instead of as a name left off (mobile/src/lib/orgToday.js).
 */
export function needsInstructor(m) {
  return m.status !== "cancelled" && !todayTeacher(m);
}

/** Where a meeting's row goes: its class page with that meeting open, as the calendar's "Open class" does. */
export function todayMeetingPath(m) {
  return `/org/classes/${m.class_id}?tab=calendar${m.occurrence_id ? `&meeting=${m.occurrence_id}` : ""}`;
}
